// ---------------------------------------------------------------------------
// SocialFeed Renderer (editor key: SocialFeed)
// ---------------------------------------------------------------------------
// Read-only grid of recent social posts parsed from a pipe-delimited
// textarea ("platform | author | caption | postUrl | imageUrl" per line).
// Published as plain markup — no embed iframes, no inline JS, no third-
// party tracking pixels. Platform names are constrained to a safelist so
// the class hook can drive brand-specific styling without leaking arbitrary
// strings into the DOM. Hrefs and image sources route through the
// existing `url()` / `imageUrl()` validators so `javascript:` payloads
// resolve to `#` and unsupported `data:` URIs are dropped.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text, url, imageUrl } from "../escape.js";

interface SocialFeedProps {
  heading?: string;
  posts?: string;
  columns?: number | string;
}

interface SocialPost {
  platform: string;
  author: string;
  caption: string;
  postUrl: string;
  imageUrl: string;
}

const ALLOWED_PLATFORMS = new Set([
  "instagram",
  "facebook",
  "twitter",
  "x",
  "tiktok",
  "linkedin",
  "youtube",
]);

function normalizePlatform(value: string): string {
  const lowered = value.trim().toLowerCase();
  return ALLOWED_PLATFORMS.has(lowered) ? lowered : "social";
}

function normalizeColumns(value: unknown): number {
  const parsed =
    typeof value === "number" ? value : Number.parseInt(String(value ?? ""), 10);
  if (!Number.isFinite(parsed)) return 3;
  return Math.min(Math.max(Math.round(parsed), 1), 6);
}

function parsePosts(value: unknown): SocialPost[] {
  if (typeof value !== "string") return [];
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => {
      const [platform, author, caption, postUrl, image] = line.split("|");
      return {
        platform: normalizePlatform(platform ?? ""),
        author: (author ?? "").trim(),
        caption: (caption ?? "").trim(),
        postUrl: (postUrl ?? "").trim(),
        imageUrl: (image ?? "").trim(),
      };
    })
    .filter((post) => post.author.length > 0 || post.caption.length > 0);
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as SocialFeedProps;

  const posts = parsePosts(p.posts);
  if (posts.length === 0) {
    return "<!-- social-feed missing posts -->";
  }

  const heading = typeof p.heading === "string" ? p.heading.trim() : "";
  const columns = normalizeColumns(p.columns);

  const headingHtml = heading
    ? `\n  <h2 class="ease-social-feed__heading">${text(heading)}</h2>`
    : "";

  const items = posts
    .map((post) => {
      const imgHtml = post.imageUrl
        ? `\n      <img class="ease-social-feed__image" src="${imageUrl(post.imageUrl)}" alt="" loading="lazy" />`
        : "";
      const captionHtml = post.caption
        ? `\n      <p class="ease-social-feed__caption">${text(post.caption)}</p>`
        : "";
      const authorHtml = post.author
        ? `\n      <p class="ease-social-feed__author">${text(post.author)}</p>`
        : "";

      const inner = `<article class="ease-social-feed__card" data-ease-platform="${attr(post.platform)}">${imgHtml}${authorHtml}${captionHtml}
    </article>`;

      if (post.postUrl) {
        return `    <li class="ease-social-feed__item">\n      <a class="ease-social-feed__link" href="${url(post.postUrl)}" rel="noopener noreferrer" target="_blank">${inner}</a>\n    </li>`;
      }
      return `    <li class="ease-social-feed__item">\n      ${inner}\n    </li>`;
    })
    .join("\n");

  return `<section class="ease-section ease-social-feed ease-social-feed--cols-${attr(String(columns))}" data-ease-columns="${attr(String(columns))}">${headingHtml}
  <ul class="ease-social-feed__list" role="list">
${items}
  </ul>
</section>`;
}

registerRenderer("social-feed", { render });
